import type { ComponentType, ReactNode } from 'react'
import { Navigate } from 'react-router-dom'

import { useUserStore } from '@/store'
import { resolveHomeDestination } from '@/features/auth/utils/resolveHomeDestination'

import {
  ROUTE_POLICIES,
  getAllRequiredPermissions,
  getRoutePolicyById,
} from '@/constants'
import { LazyRoute } from './LazyRoute'
import { RequirePermissions } from './RequirePermissions'
import { createLazyPageFromNamed } from './createLazyPage'

import { DashboardPageSkeleton } from '@/features/dashboard/components/DashboardPageSkeleton'
import { CheckoutPageSkeleton } from '@/features/checkout/components/CheckoutPageSkeleton'
import { ProductsPageSkeleton } from '@/features/products/components/ProductsPageSkeleton'
import { SalesHistoryPageSkeleton } from '@/features/sales/components/SalesHistoryPageSkeleton'
import { InventoryLotsPageSkeleton } from '@/features/inventory/components/InventoryLotsPageSkeleton'
import { ReportsStockPageSkeleton } from '@/features/reports/components/ReportsStockPageSkeleton'
import { UsersPageSkeleton } from '@/features/users/components/UsersPageSkeleton'
import { RolesPermissionsPageSkeleton } from '@/features/roles-permissions/components/RolesPermissionsPageSkeleton'
import { SettingsPageSkeleton } from '@/features/settings/components/SettingsPageSkeleton'

const DashboardPage = createLazyPageFromNamed(
  () => import('@/features/dashboard/DashboardPage'),
  'DashboardPage',
)
const CheckoutPage = createLazyPageFromNamed(
  () => import('@/features/checkout/CheckoutPage'),
  'CheckoutPage',
)
const SalesHistoryPage = createLazyPageFromNamed(
  () => import('@/features/sales/SalesHistoryPage'),
  'SalesHistoryPage',
)
const ProductsPage = createLazyPageFromNamed(
  () => import('@/features/products/ProductsPage'),
  'ProductsPage',
)
const InventoryLotsPage = createLazyPageFromNamed(
  () => import('@/features/inventory/InventoryLotsPage'),
  'InventoryLotsPage',
)
const ReportsStockPage = createLazyPageFromNamed(
  () => import('@/features/reports/ReportsStockPage'),
  'ReportsStockPage',
)
const UsersPage = createLazyPageFromNamed(
  () => import('@/features/users/UsersPage'),
  'UsersPage',
)
const RolesPermissionsPage = createLazyPageFromNamed(
  () => import('@/features/roles-permissions/RolesPermissionsPage'),
  'RolesPermissionsPage',
)
const SettingsPage = createLazyPageFromNamed(
  () => import('@/features/settings/SettingsPage'),
  'SettingsPage',
)
const AppearancePage = createLazyPageFromNamed(
  () => import('@/features/settings/components/AppearancePage'),
  'AppearancePage',
)

const lazyPages: Record<string, ComponentType> = {
  dashboard: DashboardPage,
  checkout: CheckoutPage,
  'sales-history': SalesHistoryPage,
  'products-catalog': ProductsPage,
  'inventory-lots': InventoryLotsPage,
  'reports-stock': ReportsStockPage,
  users: UsersPage,
  'roles-permissions': RolesPermissionsPage,
}

const lazyPageFallbacks: Record<string, ReactNode> = {
  dashboard: <DashboardPageSkeleton />,
  checkout: <CheckoutPageSkeleton />,
  'sales-history': <SalesHistoryPageSkeleton />,
  'products-catalog': <ProductsPageSkeleton />,
  'inventory-lots': <InventoryLotsPageSkeleton />,
  'reports-stock': <ReportsStockPageSkeleton />,
  users: <UsersPageSkeleton />,
  'roles-permissions': <RolesPermissionsPageSkeleton />,
}

export function ProtectedLazy({ loaderKey }: { loaderKey: string }) {
  const Component = lazyPages[loaderKey]
  if (!Component) {
    throw new Error(`No lazy page registered for "${loaderKey}".`)
  }
  const policy = getRoutePolicyById(
    loaderKey as (typeof ROUTE_POLICIES)[number]['id'],
  )
  return (
    <RequirePermissions requiredPermissions={getAllRequiredPermissions(policy)}>
      <LazyRoute Component={Component} fallback={lazyPageFallbacks[loaderKey]} />
    </RequirePermissions>
  )
}

export function SettingsRoute() {
  return <LazyRoute Component={SettingsPage} fallback={<SettingsPageSkeleton />} />
}

export function AppearanceRoute() {
  return <LazyRoute Component={AppearancePage} fallback={<SettingsPageSkeleton />} />
}

export function HomeRedirect() {
  const currentUser = useUserStore((state) => state.currentUser)

  return <Navigate to={resolveHomeDestination(currentUser)} replace />
}
